<script setup>
import {computed, ref, onMounted} from "vue";
import {useRouter} from "vue-router";
import {useAuthenticationStore} from "@/stores/authentication.js";
import createApiService from "@/services/api.js";
import {definePage} from "unplugin-vue-router/runtime";
import eyeIcon from "@/assets/eye.svg";
import eyeSlashIcon from "@/assets/eye-slash.svg";
import editRoundIcon from "@/assets/edit-round.svg";
import imageIcon from "@/assets/image.svg";
import {useAdminStore} from "@/stores/admin.js";
import {initializeApp} from "https://www.gstatic.com/firebasejs/11.4.0/firebase-app.js";
import {getMessaging, getToken} from "https://www.gstatic.com/firebasejs/11.4.0/firebase-messaging.js";

definePage({
  meta: {
    title: 'Mijn account',
    requiresAuth: true,
    layout: 'default',
  },
})

const router = useRouter();
const api = createApiService(router);
const authStore = useAuthenticationStore();
const adminStore = useAdminStore();

const profileImage = ref(null)
const newProfileImage = ref(null);

const userId = ref(null);
const firstName = ref(null)
const insertion = ref(null)
const lastName = ref(null)
const email = ref(null)
const phone = ref(null)

const password = ref('');
const passwordRepeat = ref('');
const showPassword = ref(false);

const infoMessage = ref(null);
const passwordMessage = ref(null);

const fullName = computed(() => {
  let fullName = firstName.value + ' ';
  if (insertion.value.length > 0) {
    fullName += insertion.value + ' ';
  }
  fullName += lastName.value;

  return fullName;
});

const setProfileData = () => {
  let mode = authStore.getMode;
  let user = (mode === 'admin' || (!authStore.isAdmin)) ? authStore.currentUser : adminStore.getClientUser;

  profileImage.value = user?.image;
  userId.value = user.id;
  firstName.value = user.firstName;
  insertion.value = user.insertion;
  lastName.value = user.lastName;
  email.value = user.email;
  phone.value = user.phone;

  digitalNumbers.value = authStore.isDigitalNumbers;
  currentToken.value = authStore.getDeviceToken;
}

const loading = ref(false);
const infoErrors = ref([]);
const passwordErrors = ref([]);
const mappedErrors = ref({});

const saveChanges = async () => {
  loading.value = true;
  infoErrors.value = [];
  mappedErrors.value = {};
  infoMessage.value = null;

  window.scrollTo({top: 0, left: 0, behavior: 'smooth'});

  const formData = new FormData();
  formData.append('image', newProfileImage.value);
  formData.append('id', userId.value);
  formData.append('firstName', firstName.value);
  formData.append('insertion', insertion.value);
  formData.append('lastName', lastName.value);
  formData.append('email', email.value);
  formData.append('phone', phone.value);

  try {
    const response = await api.post('saveaccountdata', formData, {headers: {'Content-Type': 'multipart/form-data'}});

    if (response.status === 200) {
      if ((authStore.getMode === 'admin' || (!authStore.isAdmin))) { // update data
        authStore.setLoginData(response.data.data, true, false);
      }
      else { // update client data
        adminStore.setClientUser(response.data.data);
      }

      infoMessage.value = {message: response.data.message, type: 'success'};
    }
    else {
      infoErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    if (e.response?.status === 400) {
      if (Object.keys(e.response.data?.data).length > 0) {
        mappedErrors.value = e.response.data.data;
        for (const error in e.response.data.data) {
          infoErrors.value.push(e.response.data.data[error]);
        }
      }
      else {
        infoErrors.value.push(e.response.data.message);
      }
    }
    else {
      infoErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } finally {
    loading.value = false;
  }
}

const updatePassword = async () => {
  loading.value = true;
  passwordErrors.value = [];
  mappedErrors.value = {};
  passwordMessage.value = null;

  try {
    const response = await api.post('updatepassword', {
      id: userId.value,
      password: password.value,
      passwordRepeat: passwordRepeat.value
    });

    if (response.status === 200) {
      passwordMessage.value = {message: response.data.message, type: 'success'};
    }
    else {
      passwordErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    if (e.response?.status === 400) {
      if (Object.keys(e.response.data?.data).length > 0) {
        mappedErrors.value = e.response.data.data;
        for (const error in e.response.data.data) {
          passwordErrors.value.push(e.response.data.data[error]);
        }
      }
      else {
        passwordErrors.value.push(e.response.data.message);
      }
    }
    else {
      passwordErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } finally {
    loading.value = false;
  }
}

const logout = async () => {
  try {
    const response = await api.post('logout');

    if (response.status !== 200) {
      throw new Error('Er is iets misgegaan!')
    }

    if (authStore.mayViewAdmin || authStore.isAdmin) {
      adminStore.setClient(null)
      adminStore.setClientUser(null)
    }
    authStore.logOut();

    await router.push('/inloggen');
  } catch (e) {
    console.error(e);
  }
}

const imageUrl = ref('');

const createImage = (file) => {
  const reader = new FileReader();

  reader.onload = e => {
    imageUrl.value = e.target.result;
  };
  reader.readAsDataURL(file);
};

const onFileChange = (file) => {
  if (!file) {
    return;
  }
  createImage(file);
}

const changeMode = async () => {
  let mode = (authStore.getMode === 'admin') ? 'client' : 'admin';
  authStore.setMode(mode);

  await router.push('/');
}

const digitalNumbers = ref(true);
const changeTimeMode = async () => {
  loading.value = true;
  digitalNumbers.value = !digitalNumbers.value;

  try {
    const response = await api.post('changeTimeMode', {
      id: userId.value,
      digitalNumbers: digitalNumbers.value,
    });

    if (response.status === 200) {
      authStore.setDigitalNumbers(digitalNumbers.value);
      infoMessage.value = {message: response.data.message, type: 'success'};
    }
    else {
      infoErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } catch (e) {
    if (e.response?.status === 400) {
      if (Object.keys(e.response.data?.data).length > 0) {
        mappedErrors.value = e.response.data.data;
        for (const error in e.response.data.data) {
          infoErrors.value.push(e.response.data.data[error]);
        }
      }
      else {
        infoErrors.value.push(e.response.data.message);
      }
    }
    else {
      infoErrors.value = ['Er is iets misgegaan. Probeer het later nog eens.'];
    }
  } finally {
    loading.value = false;
  }
};

// import from .env in root
const configSettings = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const currentToken = ref(null);
const newToken = ref(null);

const initFirebase = async () => {
  if (!configSettings || Object.keys(configSettings).length === 0) {
    alert('Er is iets misgegaan met het ophalen van de configuratie.')
    return;
  }

  const firebaseConfig = {
    apiKey: configSettings.apiKey,
    authDomain: configSettings.authDomain,
    projectId: configSettings.projectId,
    storageBucket: configSettings.storageBucket,
    messagingSenderId: configSettings.messagingSenderId,
    appId: configSettings.appId,
  };
  const vapidKey = configSettings.vapidKey;

  // Initialize Firebase
  const app = initializeApp(firebaseConfig);
  const messaging = getMessaging(app);

  const isDev = import.meta.env.MODE === 'development'

  let swPath = 'https://www.stapvoorstapregie.nl';
  if (isDev) {
    swPath = 'https://stapvoorstapregie.nl.zie.localhost';
  }
  swPath += '/projects/zie/includes/jsscripts/';

  const registration = await navigator.serviceWorker.getRegistration(swPath);
  // navigator.serviceWorker.ready
  await getToken(messaging, {
    serviceWorkerRegistration: registration,
    vapidKey: vapidKey
  }).then(async (deviceToken) => {
    newToken.value = deviceToken;
  })
}

const deviceMessage = ref(null);
const updateDeviceToken = async () => {
  if (currentToken.value || currentToken.value === newToken.value) {
    await unregisterDevice();
  } else {
    await registerDevice();
  }
}

const unregisterDevice = async() => {
  loading.value = true;
  deviceMessage.value = null;

  try {
    const response = await api.post('deletefirebasedevicetoken', {
      userId: userId.value,
      token: newToken.value,
    });

    if (response.status === 200) {
      authStore.setDeviceToken(null);
      currentToken.value = null;
      deviceMessage.value = {message: response.data.message, type: 'success'};
    }
    else {
      deviceMessage.value = {message: response.data.message, type: 'error'};
    }
  } catch (e) {
    deviceMessage.value = {message: 'Er is iets misgegaan. Probeer het later nog eens.', type: 'error'};
  } finally {
    loading.value = false;
  }
};

const registerDevice = async () => {
  loading.value = true;
  deviceMessage.value = null;

  try {
    const response = await api.post('savefirebasedevicetoken', {
      userId: userId.value,
      token: newToken.value,
    });

    if (response.status === 200) {
      authStore.setDeviceToken(response.data.data);
      currentToken.value = authStore.getDeviceToken;
      deviceMessage.value = {message: response.data.message, type: 'success'};
    }
    else {
      deviceMessage.value = {message: response.data.message, type: 'error'};
    }
  } catch (e) {
    deviceMessage.value = {message: 'Er is iets misgegaan. Probeer het later nog eens.', type: 'error'};
  } finally {
    loading.value = false;
  }
};

const notificationsEnabled = ref(false);
onMounted(async () => {
  setProfileData();
  if (!("Notification" in window)) { // browser not supported
    notificationsEnabled.value = false;
    alert('Let op! Deze browser ondersteunt geen meldingen.');
  } else if (Notification.permission === "granted") { // check if already granted
    notificationsEnabled.value = true;
    await initFirebase();
  } else if (Notification.permission !== "denied") { // ask for permission
    Notification.requestPermission().then(async (permission) => {
      notificationsEnabled.value = false;
      if (permission === "granted") {
        notificationsEnabled.value = true;
        await initFirebase();
      }
    });
  }
})

</script>
<template>
  <admin-header v-if="authStore.getMode === 'admin'"/>
  <client-header v-else/>

  <the-loader v-if="loading"/>

  <v-card
    class="ma-auto mt-4"
    color="primary"
    max-width="500"
  >
    <p class="text-center mb-4" v-if="authStore.getMode === 'admin' && !authStore.mayViewAdmin">Gegevens zichtbaar voor
      client:</p>
    <v-form @submit.prevent="saveChanges" class="px-2">

      <v-alert
        v-if="infoMessage"
        class="mx-auto mb-5"
        max-width="500"
        :color="infoMessage.type"
        variant="elevated"
      >
        {{ infoMessage.message }}
      </v-alert>

      <v-alert
        v-if="infoErrors.length > 0"
        color="error"
        variant="elevated"
        class="mx-auto mb-4"
        max-width="500"
      >
        <template #text>
            <span
              class="d-block"
              v-for="(error, index) in infoErrors"
              :key="index"
            >
              {{ error }}
            </span>
        </template>
      </v-alert>

      <div
        class="position-relative mb-4 cursor-pointer ma-auto"
        style="width: fit-content"
        @click="$refs.fileInputRef.click()"
      >
        <v-avatar
          v-if="profileImage && !newProfileImage"
          :image="profileImage"
          size="80" class="mx-auto d-block"
        />
        <v-avatar
          v-else
          :image="(imageUrl?.length > 0) ? imageUrl : imageIcon"
          class="mx-auto d-block"
          size="80"
        />
        <v-file-input
          v-model="newProfileImage"
          class=" position-absolute bottom-0 left-0 right-0 mx-auto d-block"
          :prepend-icon="null"
          ref="fileInputRef"
          accept="image/*"
          hide-input
          :disabled="loading"
          @update:model-value="onFileChange"
        >
          <template #prepend>
            <v-img
              title="bewerken"
              :src="editRoundIcon"
              style="transform: translateX(40px);"
              width="28" height="27"
            />
          </template>
        </v-file-input>
      </div>

      <div class="mb-4">
        <h4 class="mb-2">
          Voornaam<span :class="mappedErrors?.firstName ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="firstName"
          placeholder="Voornaam"
          variant="solo"
          :disabled="loading"
          counter="100"
          max="100"
          required hide-details
        />
      </div>

      <div class="mb-4">
        <h4 class="mb-2">
          Tussenvoegsel<span :class="mappedErrors?.insertion ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="insertion"
          placeholder="Tussenvoegsel"
          variant="solo"
          hide-details
          :disabled="loading"
        />
      </div>

      <div class="mb-4">
        <h4 class="mb-2">
          Achternaam<span :class="mappedErrors?.lastName ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="lastName"
          placeholder="Achternaam"
          variant="solo"
          required hide-details
          :disabled="loading"
        />
      </div>

      <div class="mb-4">
        <h4 class="mb-2">Telefoonnummer</h4>
        <v-text-field
          v-model="phone"
          placeholder="Telefoonnummer"
          variant="solo"
          required hide-details
          :disabled="loading"
        />
      </div>

      <div class="mb-4">
        <h4 class="mb-2">
          E-mailadres<span :class="mappedErrors?.email ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="email"
          placeholder="E-mailadres"
          variant="solo"
          required hide-details
          :disabled="loading"
        />
      </div>

      <v-btn
        class="my-8 w-100"
        height="54"
        type="submit"
        color="secondary"
        variant="elevated"
        block
        :disabled="loading"
      >
        <h4>Opslaan</h4>
      </v-btn>
    </v-form>

    <hr class="w-100 my-10" id="page-spacer">

    <p class="text-center mb-4">Wachtwoord wijzigen:</p>
    <v-form @submit.prevent="updatePassword" class="px-2 mb-8">

      <v-alert
        v-if="passwordMessage"
        class="mx-auto mb-5"
        max-width="500"
        :color="passwordMessage.type"
        variant="elevated"
      >
        {{ passwordMessage.message }}
      </v-alert>

      <v-alert
        v-if="passwordErrors.length > 0"
        color="error"
        variant="elevated"
        class="mx-auto mb-4"
        max-width="500"
      >
        <template #text>
            <span
              class="d-block"
              v-for="(error, index) in passwordErrors"
              :key="index"
            >
              {{ error }}
            </span>
        </template>
      </v-alert>

      <div class="mb-4">
        <h4 class="mb-2">
          Nieuw wachtwoord<span :class="mappedErrors?.password ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="password"
          placeholder="Nieuw wachtwoord"
          variant="solo"
          :type="showPassword ? 'text' : 'password'"
          hide-details
          :disabled="loading"
        >
          <template #append-inner>
            <v-icon
              :title="showPassword ? 'Verbergen' : 'Tonen'"
              @click="showPassword = !showPassword"
              class="cursor-pointer opacity-100"
            >
              <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
            </v-icon>
          </template>
        </v-text-field>
      </div>

      <div class="mb-8">
        <h4 class="mb-2">
          Herhaal wachtwoord<span :class="mappedErrors?.password ? 'show-asterisk' : 'hide-asterisk'">*</span>
        </h4>
        <v-text-field
          v-model="passwordRepeat"
          placeholder="Herhaal wachtwoord"
          variant="solo"
          :type="showPassword ? 'text' : 'password'"
          hide-details
          :disabled="loading"
        >
          <template #append-inner>
            <v-icon
              :title="showPassword ? 'Verbergen' : 'Tonen'"
              @click="showPassword = !showPassword"
              class="cursor-pointer opacity-100"
            >
              <v-img :src="showPassword ? eyeIcon : eyeSlashIcon"></v-img>
            </v-icon>
          </template>
        </v-text-field>
      </div>

      <v-btn
        class="mb-2 w-100"
        height="54"
        type="submit"
        color="secondary"
        variant="elevated"
        block
        :disabled="loading"
      >
        <h4>Wachtwoord opslaan</h4>
      </v-btn>
    </v-form>

    <hr class="w-100 my-10" id="page-spacer">

    <template v-if="authStore.getMode !== 'admin'">
      <div class="px-2">
        <p class="text-center mb-4">Meldingen:</p>
        <v-alert
          v-if="deviceMessage"
          class="mx-auto mb-5"
          max-width="500"
          :color="deviceMessage.type"
          variant="elevated"
          :text="deviceMessage.message"
        />

        <v-alert
          v-if="!notificationsEnabled"
          class="mx-auto mb-5"
          max-width="500"
          color="error"
          text="Je hebt meldingen geblokkeerd. Geef toestemming in je browserinstellingen om meldingen te ontvangen."
        />
        <template v-else>
          <v-alert
            v-if="currentToken && currentToken !== newToken"
            class="mx-auto mb-5"
            max-width="500"
            color="error"
            text="Let op! Je hebt een ander apparaat gekoppeld aan je account."
          />
          <v-btn
            class="mb-2 w-100"
            height="54"
            variant="elevated"
            color="secondary"
            @click="updateDeviceToken"
            :disabled="!notificationsEnabled"
          ><h4>Apparaat <span v-if="(currentToken && currentToken !== newToken) || currentToken === newToken">ont</span>koppelen</h4></v-btn>
        </template>
      </div>

      <hr class="w-100 my-10" id="page-spacer">
    </template>

    <div class="px-2">
      <p class="text-center mb-4">Acties:</p>

      <v-btn
        class="mb-2 w-100"
        height="54"
        variant="elevated"
        color="secondary"
        @click="changeTimeMode"
      ><h4>{{ digitalNumbers ? 'Geschreven' : 'Digitale' }} tijdsweergave</h4></v-btn>
      <v-btn
        v-if="(authStore.isAdmin && adminStore.getClientUser) || authStore.mayViewAdmin"
        class="mb-2 w-100"
        height="54"
        variant="elevated"
        color="secondary"
        @click="changeMode"
      ><h4>{{ authStore.getMode === 'admin' ? 'Cliënt' : 'Admin' }} weergave</h4></v-btn>

      <v-btn
        class="mb-2 w-100"
        height="54"
        variant="elevated"
        color="secondary"
        @click="logout"
      ><h4>Uitloggen</h4></v-btn>
    </div>

  </v-card>

  <div style="margin-bottom: 100px"/>
  <AdminFooter v-if="authStore.getMode === 'admin'"/>
  <ClientFooter v-else/>

</template>

<style scoped>
#page-spacer {
  height: 1px;
  border: none;
  background-color: #FFFFFF40
}

.show-asterisk {
  display: inline;
  color: rgb(var(--v-theme-error));
}

.hide-asterisk {
  display: none;
}
</style>
